import React from 'react';
import { Box, Card, CardContent, Typography } from '@mui/material';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';
import { BusinessTypeDistribution as BusinessTypeDistributionType } from '../../../types/utility.types';

interface BusinessTypeDistributionProps {
  data: BusinessTypeDistributionType[];
  isLoading: boolean;
}

// Standardized color mapping for business types
const BUSINESS_TYPE_COLORS: Record<string, string> = {
  'residential': '#0088FE', // Blue
  'commercial': '#00C49F', // Green
  'industrial': '#FFBB28', // Yellow
  'small_business': '#FF8042', // Orange
  'enterprise': '#8884d8', // Purple
  'government': '#82ca9d', // Light green
  'non_profit': '#ffc658', // Light orange
  'default': '#9e9e9e' // Gray for unknown types
};

const getBusinessTypeColor = (type: string): string => {
  const normalizedType = type.toLowerCase().replace(/\s+/g, '_');
  return BUSINESS_TYPE_COLORS[normalizedType] || BUSINESS_TYPE_COLORS.default;
};

const BusinessTypeDistribution: React.FC<BusinessTypeDistributionProps> = ({ data, isLoading }) => {
  if (isLoading) {
    return (
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Business Type Distribution
          </Typography>
          <Box sx={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography>Loading...</Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  // Filter out any items with count 0 and sort by count (descending)
  const chartData = data
    .filter(item => item.count > 0)
    .sort((a, b) => b.count - a.count);

  const totalCount = chartData.reduce((sum, item) => sum + item.count, 0);

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Business Type Distribution
        </Typography>
        {chartData.length > 0 ? (
          <Box sx={{ height: 300 }}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={chartData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="count"
                  nameKey="type"
                >
                  {chartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={getBusinessTypeColor(entry.type)} />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(value: number, name: string) => [
                    `${value} (${Math.round((value / totalCount) * 100)}%)`,
                    name
                  ]}
                  labelFormatter={(label) => `Business Type: ${label}`}
                  contentStyle={{
                    backgroundColor: '#333',
                    border: 'none',
                    borderRadius: '8px',
                    color: '#fff'
                  }}
                />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </Box>
        ) : (
          <Box sx={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography>No data available</Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default BusinessTypeDistribution;
